<script setup lang="ts">
import { repository } from "~/repository";
import { map, groupBy, mapValues, sortBy, flow, first } from "lodash-es";
import moment from "moment";
import DateNavigator from "~/components/Schedule/DateNavigator.vue";
import type { Collection } from "~/types";

useSeoMeta({
  title: "Schedule | PokerGO",
  ogTitle: "Schedule | PokerGO",
  twitterTitle: "Schedule | PokerGO",
});

const { data, pending } = await useAsyncData(
  () => repository.common.fetchViewData({ viewName: "live" }),
  {
    lazy: true,
    server: false,
    transform: (response) => {
      const collection_list = response.data.collection_list || [];

      const processCollections = flow(
        (list: any[]) =>
          map(list, (item) => {
            const airDate = moment(item.air_date);

            return {
              ...item,
              group: airDate.format("MMMM YYYY"),
              subGroup: airDate.format("MMMM DD, YYYY"),
            };
          }),

        (mappedList: Array<{ group: string; subGroup: string } & Collection>) =>
          groupBy(mappedList, "group"),

        (
          groupedByMonthYear: Record<
            string,
            Array<{ group: string; subGroup: string } & Collection>
          >,
        ) =>
          mapValues(groupedByMonthYear, (group) => sortBy(group, "aired_at")),
      );

      const collectionsGroupedBy = processCollections(
        sortBy(collection_list, "aired_at"),
      );

      return {
        collectionsGroupedBy,
        groups: Object.keys(collectionsGroupedBy),
      };
    },
  },
);

function getGroupedBySubGroup(item: Collection & { subGroup: string }) {
  return groupBy(item, "subGroup");
}

const selectedGroup = ref("");

watch(
  () => data.value?.groups,
  () => {
    const valueForCurrentMonth = moment().format("MMMM YYYY");
    if (data.value?.groups.includes(valueForCurrentMonth)) {
      selectedGroup.value = valueForCurrentMonth;
    } else {
      selectedGroup.value = first(data.value?.groups) || "";
    }
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <div class="h-20"></div>
    <div v-if="pending && !data" class="mt-20 text-center">
      <ProgressSpinner class="!h-20 !w-20" />
    </div>
    <div v-else class="lg-px-0 mx-auto mt-10 max-w-screen-lg px-3">
      <Banner page="schedule" location="header" />
      <DateNavigator
        v-model:selected-group="selectedGroup"
        :groups="data?.groups || []"
      />

      <div>
        <div class="mb-10">
          <div class="flex flex-col gap-4">
            <div
              v-for="(dailyData, subGroupName) in getGroupedBySubGroup(
                data?.collectionsGroupedBy?.[selectedGroup] || [],
              )"
              :key="subGroupName"
            >
              <div class="mb-5 text-2xl font-bold">
                {{ subGroupName }}
              </div>
              <div class="flex flex-col gap-5">
                <div
                  v-for="(item, idx) in dailyData"
                  :key="`schedule_item_${idx}`"
                  class="flex"
                >
                  <ScheduleItem
                    v-if="item"
                    :id="(item as Collection).id"
                    :key="`item_${idx}_${idx}`"
                    class="w-full"
                    :label="(item as Collection).label"
                    :type="(item as Collection).type"
                    :title="(item as Collection).title"
                    :subheading="(item as Collection).subheading"
                    :thumbnail="
                      getThumbnailImage([
                        (item as Collection).pre_air_images,
                        (item as Collection).images,
                      ])
                    "
                    :notification-set="(item as Collection).notification_set"
                    :air-date="(item as Collection).aired_at"
                    :end-date="(item as Collection).ended_at"
                    :video-type="(item as Collection).video_type"
                    :is-airing-today="(item as Collection).is_airing_today"
                    :is-live="(item as Collection).is_live"
                    :is-new-episode="(item as Collection).is_new_episode"
                    date-format="hh:mm A"
                    :commentary-by="(item as Collection)?.commentary_by"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.overflow-x-auto {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */
}

.overflow-x-auto::-webkit-scrollbar {
  display: none; /* Chrome, Safari, and Opera */
}
</style>
